import SPKToast from '$components/SPKToast/SPKToast';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import {
  useGetCompanyInfoQuery,
  useUpdateCompaniesMutation,
} from '$store/modules/companies';
import { OrganizationSizeTypes } from '$types/position/positionPropertyValues';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { SelectButton } from 'primereact/selectbutton';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const SPKAddPositionCompleted = ({
  onComplete,
}: {
  onComplete: () => void;
}) => {
  const [t] = useTranslation('global');
  const {
    employerAddPositionForm: { JobPosition, jobTitle },
  } = useAppSelector((state) => state.company);

  const { data: GetCompanyInfoData, isLoading: IsLoadingCompanyInfoData } =
    useGetCompanyInfoQuery();
  const [updateCompany] = useUpdateCompaniesMutation();

  const { serverEmployer } = useAppSelector((state) => state.auth);
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      companyUrl: '',
      companyDescription: '',
      organizationSize: '',
    },
  });

  const showForm = useMemo(() => {
    if (IsLoadingCompanyInfoData) return false;
    if (GetCompanyInfoData) {
      const { Description, OrganizationSize } = GetCompanyInfoData;
      if (!Description || !OrganizationSize) {
        reset({
          companyDescription: Description || '',
          organizationSize: OrganizationSize || '',
        });
        return true;
      }
    }
    return false;
  }, [GetCompanyInfoData, IsLoadingCompanyInfoData, reset]);

  const onSuccessSubmitHandler = ({
    companyDescription,
    companyUrl,
    organizationSize,
  }: any) => {
    updateCompany({
      Description: companyDescription,
      OrganizationSize: organizationSize,
      companyId: serverEmployer?.Company.Id as number,
      Url: companyUrl,
    });
    onComplete();
  };

  const onErrorSubmitHandler = () => {
    SPKToast(t('EmployerAddPosition.Errors.MissingRequiredFields'), {
      type: 'Error',
    });
  };

  return (
    <div className="flex flex-col gap-8 justify-center text-center px-4 lg:px-0 mt-32 md:mt-24">
      <div>
        <i className="pi pi-verified text-spk-scores-highContrast text-5xl" />
        <h1 className="text-5xl font-extrabold">
          {t('EmployerAddPosition.PositionCreated')}
        </h1>
      </div>
      <h3>
        {t('EmployerAddPosition.PostedPosition', {
          positionName: jobTitle || JobPosition?.title || '',
        })}
      </h3>
      {showForm && (
        <form
          onSubmit={handleSubmit(onSuccessSubmitHandler, onErrorSubmitHandler)}
          className="flex flex-col text-left gap-8"
        >
          <div className="flex flex-col text-spk-mediumContrast gap-1">
            <label htmlFor="companyUrl" className="text-xs">
              {t('EmployerAddPosition.CompanyURL')}
            </label>
            <Controller
              control={control}
              name="companyUrl"
              rules={{
                validate: (field) =>
                  !!field || t('EmployerAddPosition.Errors.RequiredField'),
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  placeholder={t('EmployerAddPosition.EnterYourCompanyWebsite')}
                  className={`!placeholder-spk-mediumContrast  ${
                    errors.companyUrl?.message
                      ? '!border-red-600 !shadow-none'
                      : 'enabled:focus:!shadow-[0_0_0_0.2rem_#a6d5fa] focus:!border-[#2196f3] hover:!border-[#2196f3]'
                  }`}
                  autoComplete="off"
                />
              )}
            />
            {errors.companyUrl?.message && (
              <span className="text-xs text-spk-brightRed">
                *{errors.companyUrl.message}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1 text-spk-mediumContrast">
            <label htmlFor="companyDescription" className="text-xs">
              {t('EmployerAddPosition.BriefCompanyDescription')}
            </label>
            <Controller
              control={control}
              name="companyDescription"
              rules={{
                validate: (field) =>
                  !!field || t('EmployerAddPosition.Errors.RequiredField'),
              }}
              render={({ field }) => (
                <InputTextarea
                  autoResize
                  placeholder={t('EmployerAddPosition.EnterYourDescription')}
                  className={`placeholder-spk-mediumContrast min-h-32 ${
                    errors.companyDescription?.message &&
                    'border-spk-brightRed focus-within:shadow-none'
                  }`}
                  {...field}
                />
              )}
            />
            {errors.companyDescription?.message && (
              <span className="text-xs text-spk-brightRed">
                *{errors.companyDescription.message}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1 text-spk-mediumContrast">
            <label htmlFor="organizationSize" className="text-xs">
              {t('EmployerAddPosition.OrganizationSize')}
            </label>
            <Controller
              control={control}
              name="organizationSize"
              rules={{
                validate: (field) =>
                  !!field || t('EmployerAddPosition.Errors.RequiredField'),
              }}
              render={({ field }) => (
                <SelectButton
                  allowEmpty={false}
                  {...field}
                  options={Object.values(OrganizationSizeTypes)}
                  pt={{
                    button: {
                      className: `${
                        errors.organizationSize?.message &&
                        'border-spk-brightRed'
                      }`,
                    },
                  }}
                />
              )}
            />
            {errors.organizationSize?.message && (
              <span className="text-xs text-spk-brightRed">
                *{errors.organizationSize.message}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="bg-employer-primary rounded-full px-4 py-2 text-spk-softBlue self-center"
          >
            {t('EmployerAddPosition.Continue')}
          </button>
        </form>
      )}
      {!showForm && (
        <button
          type="button"
          className="bg-employer-primary rounded-full px-4 py-2 text-spk-softBlue self-center"
          onClick={() => onComplete()}
        >
          {t('EmployerAddPosition.Continue')}
        </button>
      )}
    </div>
  );
};

export default SPKAddPositionCompleted;
